import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

import {Company} from 'classes/company';
import {User} from 'classes/user';
import {UserRole} from 'classes/user-role';
import { TargetGroupsHelper } from 'shared/helper/targetGroupsHelper';
import { Badge } from 'shared/models/badges/badge';
import { Content } from 'shared/models/contents/content';
import { Category } from 'shared/models/target-groups/category';
import { Group } from 'shared/models/target-groups/group';
import { TargetGroup } from 'shared/models/target-groups/target-group';
import {UserDetailsService} from './services/user-details.service';
import { ArrayType } from '@angular/compiler/src/output/output_ast';

@Component({
  selector: 'user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.css'],
})
export class UserDetailsComponent implements OnInit {

  public title: string = 'Benutzer hinzufügen';
  public user: User = new User();
  public userRoles: Array<any>; // tslint:disable-line:no-any
  public companies: Array<Company> = [];
  public contents: Dictionary<Content> = {};
  public badges: Dictionary<Badge> = {};
  public userQuestionaire: Array<any> = [];
  public confirmPassword: string = '';

  private targetGroupsHelper: TargetGroupsHelper = null;

  public allGroups: Dictionary<Group> = {};
  public selectedGroup: string = '';
  public selectableGroups: Array<string> = []; // group-ids

  public allCategories: Dictionary<Category> = {};
  public selectedCategory: string = '';
  public selectableCategories: Array<string> = [];

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private userDetailsService: UserDetailsService) { }

  public ngOnInit(): void {
    this.activatedRoute.params.map((params) => params['userId'])
      .subscribe((userId: number) => {
        if (userId) {
          this.title = 'Benutzer bearbeiten';
        }

        this.userDetailsService.getUserRoles()
          .subscribe((userRoles: Array<UserRole>) => {
            this.userRoles = userRoles;

            if (userId) {
              this.getUser(userId);
            }
          });

      });

    this.userDetailsService.getAllCompanies()
      .subscribe((companies: Array<Company>) => {
        this.companies = companies;
      });
    this.userDetailsService.getAllContents()
      .subscribe((contents: Array<Content>) => {
        contents.forEach((content) => { this.contents[content._id] = content; });
        if ( this.user ) {
          this.prepareQuestionaire();
        }
    });
    this.userDetailsService.getAllBadges()
      .subscribe((badges: Array<Badge>) => {
        badges.forEach((badge) => { this.badges[badge._id] = badge; });
    });
    this.userDetailsService.getAllTargetGroups()
      .subscribe((targetGroups: Array<TargetGroup>) => {
        this.targetGroupsHelper = new TargetGroupsHelper(targetGroups);
        if ( this.user ) {
          this.prepareQuestionaire();
        }
    });
    this.userDetailsService.getAllGroups()
      .subscribe((groups: Array<Group>) => {
        groups.forEach(group => this.allGroups[group._id+''] = group);
    });
    this.userDetailsService.getAllCategories()
      .subscribe((categories: Array<Category>) => {
        categories.forEach(category => this.allCategories[category._id+''] = category);
    });

  }

  private getUser(id: number): void {
    this.userDetailsService.getUser(id)
      .subscribe((user: User) => {
        this.user = user;
        this.confirmPassword = user.password;
        this.mapUserRolesFromUser();
        const userGroupsIds = user.groups.map(group => group._id+'');
        this.selectableGroups = Object.keys(this.allGroups).filter(key => userGroupsIds.indexOf(key) == -1);
        const userCategoriesIds = user.categories.map(category => category._id+'');
        this.selectableCategories = Object.keys(this.allCategories).filter(key => userCategoriesIds.indexOf(key) == -1);
        if ( this.contents ) {
          this.prepareQuestionaire();
        }
      });
  }

  public getTargetGroups(user: User): string {
    if ( this.targetGroupsHelper == null || user == null) {
      return "";
    }
    return this.targetGroupsHelper.getTargetGroups(user)
      .map(targetGroup => targetGroup.title)
      .join(", ");
  }

  private saveUser(): void { // tslint:disable-line
    this.userDetailsService.saveUser(this.user, this.userRoles)
      .subscribe((user: User) => {
        const modQuestionaire = this.prepareSaveQuestionaire();
        if (modQuestionaire.length == 0) {
          this.router.navigateByUrl('/users');
        }
        else {
          this.userDetailsService.modifyContents(user, modQuestionaire)
          .subscribe(() => {
            this.router.navigateByUrl('/users');
          });
        }        
      });
  }

  private updateUser(): void { // tslint:disable-line
    this.userDetailsService.updateUser(this.user, this.userRoles)
      .subscribe((user: User) => {
        const modQuestionaire = this.prepareSaveQuestionaire();
        if (modQuestionaire.length == 0) {
          this.router.navigateByUrl('/users');
        }
        else {
          this.userDetailsService.modifyContents(this.user, modQuestionaire)
          .subscribe(() => {
            this.router.navigateByUrl('/users');
          });
        }        
      });
  }

  private mapUserRolesFromUser(): void {
    for (const role of this.user.roles) {
      const componentRole: any = this.userRoles.find((userRole: UserRole) => { // tslint:disable-line:no-any
        return userRole._id === role._id;
      });

      if (componentRole) {
        componentRole.checked = true;
      }
    }
  }

  public isPasswordEqual(): boolean {
    return this.user.password === this.confirmPassword;
  }

  public selectGroup(id: string): void {
    if (id !== '') {
      this.user.groups.push(this.allGroups[id]);
      this.selectableGroups.splice(this.selectableGroups.indexOf(id), 1);
    }
  }

  public deselectGroup(id: string): void {
    this.selectableGroups.push(id);
    this.user.groups.splice(this.user.groups.indexOf(this.allGroups[id]), 1);
  }

  public selectCategory(id: string): void {
    if (id !== '') {
      this.user.categories.push(this.allCategories[id]);
      this.selectableCategories.splice(this.selectableCategories.indexOf(id), 1);
    }
  }

  public deselectCategory(id: string): void {
    this.selectableCategories.push(id);
    this.user.categories.splice(this.user.categories.indexOf(this.allCategories[id]), 1);
  }

  public deleteUnstartedContents(): void {
    this.userDetailsService.deleteUnstartedContents(this.user)
      .subscribe(() => {
        this.getUser(this.user._id);
      });
  }

  private prepareQuestionaire() {
    if (!this.targetGroupsHelper) {
      return;
    }
    const targetGroups = this.targetGroupsHelper.getTargetGroups(this.user);
    const userQuestionaire = Object.keys(this.contents)
      .map(key => this.contents[key])
      .filter(cont => cont.questionaire)
      .map(quest => { 
        const userContent = this.user.contents ? this.user.contents
          .find(cont => cont.content == quest._id) : undefined;
        const matchingGroup = targetGroups
          .find(group => quest.targetGroups
            .find(questGroup => group._id == questGroup._id));                      
        return {
          id: quest._id,
          state: (userContent !== undefined ? userContent.state : -1),
          asigned: userContent !== undefined,
          canChange: matchingGroup === undefined,
          tooltip: matchingGroup !== undefined ? 'Zugeordnet Zielgruppe: ' + matchingGroup.title : '',
          name: quest.title};
        });
    this.userQuestionaire = userQuestionaire;    
  }

  private prepareSaveQuestionaire(): Array<any> {
    return this.userQuestionaire
      .map(dat => {
        if (dat.state == -1 && dat.asigned) {
          return { type: 'add', _id: dat.id};
        }
        if (dat.state != -1 && !dat.asigned) {
          return { type: 'rm', _id: dat.id};
        }
        return {};
      })
      .filter(dat => dat.type !== undefined);

  }
}



// WEBPACK FOOTER //
// /usr/src/app/src/app/modules/user/user-details/user-details.component.ts